import { Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { get, patch } from "@/lib/api";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";

const TABS = [
  { label: "Overview", seg: "" },
  { label: "Planning", seg: "planning", to: "planning/epics" },
  { label: "Board", seg: "board" },
  { label: "Backlog", seg: "backlog" },
  { label: "Allocation", seg: "allocation" },
  { label: "Test Cases", seg: "test-cases" },
  { label: "Documents", seg: "documents" },
  { label: "Dependencies", seg: "dependencies" },
  { label: "Meeting Notes", seg: "meeting-notes" },
  { label: "Reports", seg: "reports" },
];

export default function ProjectLayout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { can } = useApp();
  const { data: project, isLoading } = useQuery({ queryKey: ["project", id], queryFn: () => get(`/pm/projects/${id}`) });

  const approve = useMutation({
    mutationFn: () => patch(`/pm/projects/${id}`, { status: "active" }),
    onSuccess: () => {
      toast.success("Project approved");
      qc.invalidateQueries({ queryKey: ["project", id] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  if (isLoading || !project) return <Skeleton className="h-96" />;

  const base = `/pm/projects/${id}`;
  const activeSeg = location.pathname.replace(base, "").replace(/^\//, "").split("/")[0];

  return (
    <div>
      <div className="mb-2 text-xs text-muted-foreground">
        <button onClick={() => navigate("/pm/projects")} className="hover:underline">Projects</button> / {project.name}
      </div>
      <div className="mb-4 flex items-center gap-3">
        <h1 className="font-display text-2xl font-bold tracking-tight">{project.name}</h1>
        <StatusBadge family={{ scrum: "blue", hybrid: "violet", lean: "green" }[project.operating_model]} label={project.operating_model} />
        <StatusBadge status={project.status} />
        {project.status === "pending_approval" && can("pm", "approve") && (
          <Button size="sm" data-testid="approve-project" onClick={() => approve.mutate()} disabled={approve.isPending}>
            {approve.isPending ? "Approving..." : "Approve"}
          </Button>
        )}
      </div>

      <div className="mb-6 flex gap-1 border-b border-border">
        {TABS.map((t) => {
          const active = activeSeg === t.seg;
          return (
            <button key={t.label} data-testid={`ptab-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => navigate(t.seg ? `${base}/${t.to || t.seg}` : base)}
              className={cn("border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")}>
              {t.label}
            </button>
          );
        })}
      </div>

      <Outlet context={{ project }} />
    </div>
  );
}
